// src/hooks/useChatSocket.js
// ━━━ 聊天 Socket.IO Hook：复用现有 socket 连接的聊天事件管理 ━━━

import { useEffect, useCallback, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import * as C from '../constants';
import api from '../api';

/**
 * 聊天专用的 Socket.IO hook
 * 管理聊天房间的加入/离开、消息收发、正在输入提示等
 * 
 * @param {object} params
 * @param {number} params.crewId - 项目组 ID
 * @param {object} params.user - 当前用户
 * @param {boolean} params.isOpen - 聊天窗口是否打开
 * @returns {object}
 */
export default function useChatSocket({ crewId, user, isOpen }) {
  const socketRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const typingTimerRef = useRef({});
  // 用 ref 镜像 messages 和 loading，使 loadHistory 不依赖频繁变化的 state
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  // 创建/复用 socket 连接
  useEffect(() => {
    if (!isOpen || !crewId || !user) return;

    const wsToken = api.getToken ? api.getToken() : localStorage.getItem('cineart_token');

    const socket = io(C.API_BASE_URL, {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      forceNew: false,
      auth: { token: wsToken },
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join-crew-chat', crewId);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    // 接收新消息
    socket.on('chat-message', (msg) => {
      setMessages(prev => {
        // 去重（防止重连时重复推送）
        if (prev.some(m => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    });

    // 消息撤回
    socket.on('chat-message-deleted', ({ messageId }) => {
      setMessages(prev => prev.filter(m => m.id !== messageId));
    });

    // 正在输入
    socket.on('chat-typing', ({ userId, username }) => {
      if (userId === user.id) return; // 忽略自己
      setTypingUsers(prev => {
        if (prev.some(u => u.userId === userId)) return prev;
        return [...prev, { userId, username }];
      });
      // 3秒后自动移除
      if (typingTimerRef.current[userId]) clearTimeout(typingTimerRef.current[userId]);
      typingTimerRef.current[userId] = setTimeout(() => {
        setTypingUsers(prev => prev.filter(u => u.userId !== userId));
        delete typingTimerRef.current[userId];
      }, 3000);
    });

    return () => {
      socket.emit('leave-crew-chat', crewId);
      socket.off('connect');
      socket.off('disconnect');
      socket.off('chat-message');
      socket.off('chat-message-deleted');
      socket.off('chat-typing');
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
      // 清理 typing timers
      Object.values(typingTimerRef.current).forEach(clearTimeout);
      typingTimerRef.current = {};
    };
  }, [isOpen, crewId, user]);

  // 加载历史消息（通过 ref 读取 messages / loading，避免每条新消息都重建回调）
  const loadHistory = useCallback(async (recipientId = null) => {
    if (!crewId || loadingRef.current) return;
    setLoading(true);
    try {
      const curMessages = messagesRef.current;
      const oldestId = curMessages.length > 0 ? curMessages[0].id : null;
      let url = `/api/crews/${crewId}/messages?limit=30`;
      if (oldestId) url += `&before=${oldestId}`;
      if (recipientId) url += `&recipientId=${recipientId}`;

      const data = await api.get(url);
      setMessages(prev => {
        // 合并，去重
        const ids = new Set(prev.map(m => m.id));
        const newMsgs = (data.messages || []).filter(m => !ids.has(m.id));
        return [...newMsgs, ...prev];
      });
      setHasMore(data.hasMore || false);
    } catch (err) {
      console.error('加载聊天历史失败:', err);
    } finally {
      setLoading(false);
    }
  }, [crewId]);

  // 初始加载
  const loadInitial = useCallback(async (recipientId = null) => {
    if (!crewId) return;
    setLoading(true);
    setMessages([]);
    try {
      let url = `/api/crews/${crewId}/messages?limit=30`;
      if (recipientId) url += `&recipientId=${recipientId}`;
      const data = await api.get(url);
      setMessages(data.messages || []);
      setHasMore(data.hasMore || false);
    } catch (err) {
      console.error('加载聊天历史失败:', err);
    } finally {
      setLoading(false);
    }
  }, [crewId]);

  // 发送消息
  const sendMessage = useCallback((data) => {
    if (!socketRef.current?.connected || !crewId) return;
    socketRef.current.emit('chat-message', {
      crewId,
      ...data,
    });
  }, [crewId]);

  // 发送正在输入
  const sendTyping = useCallback(() => {
    if (!socketRef.current?.connected || !crewId) return;
    socketRef.current.emit('chat-typing', { crewId });
  }, [crewId]);

  // 撤回消息
  const deleteMessage = useCallback(async (msgId) => {
    try {
      await api.del(`/api/crews/${crewId}/messages/${msgId}`);
    } catch (err) {
      console.error('撤回失败:', err);
      throw err;
    }
  }, [crewId]);

  return {
    messages,
    setMessages,
    typingUsers,
    connected,
    loading,
    hasMore,
    loadHistory,
    loadInitial,
    sendMessage,
    sendTyping,
    deleteMessage,
  };
}
